import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { Loader2 } from 'lucide-react';
import { useBrand } from '../brand/BrandProvider';
import { WithSteward } from '../components/WithSteward';
import { isUpdatePending, applyUpdateNow, subscribePwaUpdate } from '../pwa/pwaUpdate';
import { takeGoogleAfterSignOut, takeSignOutReturn } from '../auth/signOutReturn';
import { startGoogleSignIn } from '../auth/googleSignIn';
import { discardSignedOutCard, hostFirm, leavingThisDocument, peekSignedOutCard } from '../auth/signedOutCard';
import { SignedOutCard } from '../components/SignedOutCard';
import { RETURN_TO_KEY, SPONSORED_INVITE_PENDING_KEY } from '../components/ProtectedRoute';
import { firmDisplayName } from '../intake/IntakeBrandScope';

/**
 * Whether this bounce to /login was caused by a family clicking an invite email's accept link,
 * either flavor (copy review 2026-09-21, extended for the sponsored case steward-app#1649):
 *
 *  - An ordinary invite (`inviteEmail.ts`'s `/select-workspace?invite=<token>`): read back from
 *    `RETURN_TO_KEY`, the same way `Callback.tsx` reads it. `ProtectedRoute` writes the
 *    path+query there before redirecting here. This function does NOT clear `RETURN_TO_KEY` —
 *    only `Callback.tsx` does, once the Cognito round trip completes, because `Callback.tsx`
 *    still needs it to know where to send the user next.
 *  - A sponsored invite (`sponsoredInviteEmail.ts`'s `/select-workspace#invite=<token>`): the
 *    token rides the URL FRAGMENT, which `ProtectedRoute` deliberately never persists (the
 *    token is a workspace-admin grant), so by the time this page mounts the fragment is gone
 *    from the address bar entirely. What survives is only a yes/no flag,
 *    `SPONSORED_INVITE_PENDING_KEY`, that `ProtectedRoute` sets when the fragment it saw looked
 *    like a sponsored invite — never the token or the fragment itself.
 *
 * The sponsored flag IS read-once: it is removed unconditionally, before either branch below
 * decides what to return, so a later plain visit to /login in the same tab never keeps showing
 * the line on its account. (`Callback.tsx` also clears it, belt-and-braces, on sign-in
 * completion.) `RETURN_TO_KEY` is not read-once here for the reason above; this function only
 * reads it.
 *
 * Both signals are display-only: neither changes routing or acceptance, and the family's actual
 * pending invitations still come from `/me` on `SelectWorkspace.tsx`'s own list.
 */
function returnedForInvite(): boolean {
  try {
    // Unconditional, and read first: whichever branch below returns, the sponsored flag must not
    // outlive this call.
    const sponsoredPending = sessionStorage.getItem(SPONSORED_INVITE_PENDING_KEY) !== null;
    sessionStorage.removeItem(SPONSORED_INVITE_PENDING_KEY);
    const saved = sessionStorage.getItem(RETURN_TO_KEY) ?? '';
    if (saved.startsWith('/select-workspace') && saved.includes('invite=')) return true;
    return sponsoredPending;
  } catch {
    return false;
  }
}

/**
 * Whether this bounce to /login came from an onboarding invitation's link
 * (crossmarc/steward-app#1764, `onboardingInviteEmail.ts`'s `${APP_BASE_URL}/onboarding`). Read from
 * `RETURN_TO_KEY` like the ordinary invite above, and display-only in the same way: the person
 * is routed to `/onboarding` by `OnboardingClientGate` after sign-in, from `/me`, not by this.
 */
function returnedForOnboarding(): boolean {
  try {
    const saved = sessionStorage.getItem(RETURN_TO_KEY) ?? '';
    return saved === '/onboarding' || saved.startsWith('/onboarding/') || saved.startsWith('/onboarding?');
  } catch {
    return false;
  }
}

/**
 * Whether the bounce came from the request email's link to the requests page (steward-app#2433,
 * `/onboarding/requests`), which gets its own two lines (#50). Display-only, like the function
 * above; `returnedForOnboarding` is true for it too.
 */
function returnedForRequests(): boolean {
  try {
    const saved = sessionStorage.getItem(RETURN_TO_KEY) ?? '';
    return saved === '/onboarding/requests' || saved.startsWith('/onboarding/requests/') || saved.startsWith('/onboarding/requests?');
  } catch {
    return false;
  }
}

export function Login() {
  const auth = useOidcAuth();
  // Pre-auth, so this resolves from the preview/cached brand (docs/17 §3) —
  // corrected by /me after sign-in.
  const { shortName, kicker, isWhiteLabel } = useBrand();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [signinError, setSigninError] = useState<string | null>(null);
  // Read once: this never changes across the lifetime of this mount, and `Callback.tsx`
  // clears the underlying key as soon as sign-in completes.
  // A person who signed out from a firm's snapshot form comes back here, because this is the
  // only address the sign-in service returns a sign-out to. The note the form left is read
  // once, and they are sent on to that form's cover (`auth/signOutReturn.ts`).
  const [afterSignOut] = useState(() => takeSignOutReturn());
  // The form's "Please sign in with Google again" (#2408) signed out in full and asked for a
  // Google sign-in after it (`takeGoogleAfterSignOut`, `auth/signOutReturn.ts`). The note is
  // removed here, as this page mounts, before anything is decided from it: acted on at most
  // once, so it cannot loop.
  const [googleAfter] = useState(() => takeGoogleAfterSignOut());
  // The onboarding form's "Sign out" comes back here too (#2432) and leaves a note, which is read
  // without removing it (a render may run twice) and removed once mounted. The firm it must name
  // is the one this host resolves, or none on the shared host: a note naming any other shows the
  // ordinary page (`auth/signedOutCard.ts`). The snapshot form's note is left for its own page.
  const [hostFirmDef] = useState(() => hostFirm());
  const hostDisplayName = hostFirmDef ? firmDisplayName(hostFirmDef.brandKey, hostFirmDef.name) : null;
  const [signedOutCard, setSignedOutCard] = useState(() => peekSignedOutCard('onboarding', hostFirmDef?.firmId ?? null));
  useEffect(() => { discardSignedOutCard('onboarding'); }, []);
  /** The Google sign-in could not start: the page goes on as if there had been no request. */
  const [googleFailed, setGoogleFailed] = useState(false);
  const continuingGoogle = googleAfter !== null && !googleFailed;
  const googleStartedRef = useRef(false);
  const [invited] = useState(returnedForInvite);
  const [onboarding] = useState(returnedForOnboarding);
  const [requested] = useState(returnedForRequests);
  // Mirrors `redirecting` for the SW-update subscription below, which must not
  // reload the page while a signinRedirect() is in flight (the reload could
  // race the navigation to Cognito and cancel it).
  const redirectingRef = useRef(googleAfter !== null);

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) navigate('/', { replace: true });
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  // 🚨 Only once nobody is signed in: a sign-in found here is never replaced by this request
  // (the effect above takes that person home, and the note is already gone).
  useEffect(() => {
    if (googleAfter === null || googleStartedRef.current) return;
    if (auth.isLoading || auth.isAuthenticated) return;
    googleStartedRef.current = true;
    startGoogleSignIn(auth, googleAfter).catch(() => {
      redirectingRef.current = false;
      setGoogleFailed(true);
    });
  }, [googleAfter, auth, auth.isLoading, auth.isAuthenticated]);

  // Apply a waiting service-worker update immediately on the login page. The
  // normal policy waits for an in-app navigation, but a signed-out user never
  // navigates: a stale cached shell (e.g. one baked with a retired Cognito
  // pool) would strand them at a sign-in that can never succeed. /login is
  // pre-auth with no unsaved state, so an immediate reload is always safe.
  useEffect(() => {
    // Not while the Google sign-in asked for above is starting (its note is already used).
    // Nor while the onboarding form's own sign-out is leaving for Cognito from this document:
    // a reload makes a new document, which would consume the note before the redirect (#2432).
    if (isUpdatePending() && !redirectingRef.current && !leavingThisDocument('onboarding')) { applyUpdateNow(); return; }
    return subscribePwaUpdate(() => {
      // Once the user has clicked Sign in, let the redirect win; the update
      // applies on the next visit to /login (or the next in-app navigation).
      if (!redirectingRef.current && !leavingThisDocument('onboarding') && isUpdatePending()) applyUpdateNow();
    });
  }, []);

  // If the user redirects to Cognito and hits "back", the browser restores this
  // page from the bfcache with stale state — leaving the button stuck disabled
  // with a spinner. pageshow.persisted fires on a bfcache restore; reset there.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      // A Google sign-in started from a sign-out note (#2408) is over too: without this the
      // page would come back as the blank frame with nothing to press.
      if (e.persisted) { setRedirecting(false); redirectingRef.current = false; setGoogleFailed(true); }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // On the way to Google: nothing to read or press meanwhile (no copy of its own).
  if (continuingGoogle && !auth.isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="login-continuing-google">
        <Loader2 size={20} strokeWidth={2.25} aria-hidden className="animate-spin text-slate" />
      </div>
    );
  }

  // The onboarding form's sign-out is under way in this document and ends by leaving for
  // Cognito: nothing to press meanwhile, or a click would start a sign-in racing that redirect.
  if (!auth.isAuthenticated && leavingThisDocument('onboarding')) {
    return <div className="min-h-screen bg-cream" data-testid="login-leaving" />;
  }

  if (afterSignOut !== null && !auth.isAuthenticated) return <Navigate to={afterSignOut} replace />;

  if (signedOutCard !== null && !auth.isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-4">
        <SignedOutCard
          firmName={hostDisplayName ?? shortName}
          formName="Onboarding form"
          sent={signedOutCard.sent}
          requests={signedOutCard.requests === true}
          noun="onboarding form"
          firmInSentence={hostDisplayName ?? (isWhiteLabel ? shortName : 'your advisor')}
          onSignInAgain={() => setSignedOutCard(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream items-center justify-center px-4">
      <div className="bg-surface border border-line shadow-panel px-6 py-14 sm:px-12 w-full max-w-[400px] text-center">
        {/* Brand mark — the product leads; the kicker endorses (suppressed when
            a firm brand supplies no kicker of its own). */}
        <div className="mb-8">
          {kicker && (
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate font-sans mb-2">
              {kicker}
            </div>
          )}
          <div className="font-display text-[30px] font-semibold uppercase tracking-[0.16em] text-navy mb-3">
            {shortName}
          </div>
          <div className="w-8 h-px bg-bronze mx-auto" />
          <WithSteward className="mt-2 text-[11px] text-slate" />
        </div>

        {(invited || onboarding) && (
          <p className="text-[13px] text-navy font-semibold m-0 mb-2 leading-relaxed font-sans" data-testid={onboarding ? 'login-onboarding' : undefined}>
            {requested ? 'Your advisor has requested additional information.' : onboarding ? 'Your advisor asked for a few details.' : 'You have been invited to a Steward workspace.'}
          </p>
        )}
        <p className="text-[13px] text-slate m-0 mb-9 leading-relaxed font-sans">
          {requested
            ? 'Sign in to provide this information.'
            : onboarding
            ? 'Sign in, or create a login with the address the invitation went to, to get started.'
            : invited
              ? 'Sign in, or create an account, to see it.'
              : 'Secure access to your private financial records and documents.'}
        </p>

        <button
          onClick={() => {
            setRedirecting(true);
            redirectingRef.current = true;
            setSigninError(null);
            // If the redirect can't even start (offline, or the sign-in
            // service's discovery document is unreachable), the promise
            // rejects and no navigation happens — without the catch the
            // button would sit disabled forever with no explanation.
            //
            // extraQueryParams.prompt (W1.9): asks for the account chooser
            // instead of a silent re-auth on a shared device signed out of
            // Steward but still holding a live upstream IdP session. Whether
            // Cognito's Hosted UI forwards `prompt` through to a federated
            // IdP is unconfirmed pending a live dev Google IdP — this is a
            // no-op, not a regression, if it isn't forwarded, and the
            // Settings → Security footer note is the guaranteed mitigation
            // either way.
            auth.signinRedirect({ extraQueryParams: { prompt: 'select_account' } }).catch(() => {
              setRedirecting(false);
              redirectingRef.current = false;
              setSigninError("We couldn't reach the sign-in service. Check your connection and try again.");
            });
          }}
          disabled={redirecting}
          className="flex w-full items-center justify-center gap-2 px-6 py-[13px] bg-navy text-cream border-none rounded-token text-[11px] font-bold font-sans uppercase tracking-[0.12em] cursor-pointer disabled:cursor-wait disabled:opacity-70"
        >
          {redirecting && <Loader2 size={14} strokeWidth={2.25} aria-hidden className="animate-spin" />}
          Sign in
        </button>

        {signinError && (
          <p role="alert" className="mt-4 mb-0 text-[12px] leading-relaxed text-danger font-sans">
            {signinError}
          </p>
        )}
      </div>

      {/* Legal — relegated to the bottom, muted; suppressed under a firm brand
          (the firm leads, Steward endorses in one muted line: docs/17 §5, 2026-10-19). */}
      {!isWhiteLabel && (
        <p className="mt-6 text-[10px] text-slate font-sans">
          © 2026 Crossmarc, LLC · All rights reserved
        </p>
      )}
    </div>
  );
}
